import { useState, useEffect, useRef } from 'react';
import { useModal } from 'contexts/modal';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@apollo/client/react';
import { Grid, Button } from 'components/ui';
import { AddIcon } from 'components/uiIcons';
import InspectionSubjectsQuery from 'queries/inspection_subject/index_with_place';
import InspectionSubjectAndInspectionSubjectsQuery from 'queries/inspection_subject/index_and_show_with_place';
import OptionableAutocomplete from 'components/form/autocomplete/optionable';
import CreateInspectionSubject from 'components/inspection_subject/modal/create';

const InspectionSubject = ({
  placeId,
  id,
  label,
  name,
  onChange,
  onBlur,
  value,
  error,
  helperText,
  setValue,
}) => {
  const intl = useIntl();
  const { addModal, removeModal } = useModal();
  const [searchValue, setSearchValue] = useState(null);
  const [defaultValue, setDefaultValue] = useState(null);
  const hydratedRef = useRef(false);

  const {
    data = {
      space: { overInspectionSubjectsLimit: false },
      inspectionSubjects: { inspectionSubjects: [], inspectionSubjectsTotal: 0 },
      inspectionSubject: null,
    },
    loading,
  } = useQuery(value ? InspectionSubjectAndInspectionSubjectsQuery : InspectionSubjectsQuery, {
    variables: { page: 1, name: searchValue, placeId: placeId, inspectionSubjectId: value },
    skip: !placeId,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!loading && !hydratedRef.current && data?.inspectionSubject) {
      hydratedRef.current = true;
      setDefaultValue({ label: data.inspectionSubject.name, value: data.inspectionSubject.id });
    }
  }, [data, loading]);

  const options = data.inspectionSubjects.inspectionSubjects.map((inspectionSubject) => ({
    label: inspectionSubject.name,
    value: inspectionSubject.id,
  }));

  const onChangeInternal = (evt, value) => {
    setDefaultValue(value);
    onChange(evt, value);
  };

  const addButton = () => {
    if (data.space.overInspectionSubjectsLimit) return null;

    return (
      <Button
        onClick={() =>
          addModal(
            <CreateInspectionSubject
              handleClose={() => removeModal()}
              placeId={placeId}
              name={searchValue}
              onSuccess={(inspectionSubject) => {
                setDefaultValue({ label: inspectionSubject.name, value: inspectionSubject.id });
                removeModal();
                setValue(name, inspectionSubject.id);
              }}
            />,
            { disableAutoFocus: true, disableRestoreFocus: true }
          )
        }
        variant="contained"
      >
        <AddIcon />
      </Button>
    );
  };

  return (
    <OptionableAutocomplete
      disabled={!placeId}
      id={id}
      label={label}
      loadingText={intl.formatMessage({ id: 'autocomplete.inspection_subject.loading' })}
      noOptionsText={
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <FormattedMessage id="autocomplete.inspection_subject.no_options" />
          {addButton()}
        </Grid>
      }
      options={options}
      name={name}
      onChange={onChangeInternal}
      value={defaultValue}
      onBlur={onBlur}
      loading={loading}
      error={error}
      helperText={helperText}
      onSearch={setSearchValue}
    />
  );
};

export default InspectionSubject;
